import { PlusOutlined } from '@ant-design/icons';
import {
  ACCOUNT_TYPE_LABELS,
  currentMonth,
  DEBT_DIRECTION_LABELS,
  describeError,
  dirhams,
  isNegative,
  type MonthKey,
  monthLabel,
  present,
  TYPE_LABELS,
  today,
} from '@horof/shared';
import { Alert, Button, DatePicker, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from 'urql';
import { MonthBars } from './charts';
import { financeContext, useCanWrite, useLists } from './data';
import { monthRange } from './dates';
import { fillMonths } from './summary';
import { CockpitDocument } from './summary-data';
import { PageHeader } from './ui';

/**
 * «لوحة القيادة»: the desk's first screen. Where the money is, how the year
 * goes month by month, what is left to do, the loans not settled, and the
 * last entries.
 */
export function CockpitScreen() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const thisYear = today().slice(0, 4);
  const year = /^\d{4}$/.test(params.get('year') ?? '')
    ? (params.get('year') as string)
    : thisYear;
  const [{ data: lists }] = useLists();
  const [{ data, error }] = useQuery({
    query: CockpitDocument,
    variables: { yearFrom: `${year}-01-01`, yearTo: `${year}-12-31` },
    context: useMemo(() => financeContext, []),
    requestPolicy: 'cache-and-network',
  });

  // arqam's totals (doc 55): transfers and loan money left out.
  const totals = data?.year?.nodes[0] ?? {
    income: '0.00',
    expenses: '0.00',
    result: '0.00',
  };
  const monthNodes = data?.months?.nodes;
  const months = useMemo(
    () => fillMonths(monthNodes ?? [], year),
    [monthNodes, year],
  );
  const accounts = present(lists?.financialAccounts?.nodes);
  const debts = present(data?.debts?.nodes);
  const month = currentMonth();

  return (
    <section className="desk-page desk-summary">
      <PageHeader
        title="لوحة القيادة"
        description="أين وصلت الجمعية: السنة، ما يجب إكماله، الديون، آخر ما سُجِّل."
        actions={
          <>
            {canWrite && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/operations?panel=new')}
              >
                عملية جديدة
              </Button>
            )}
            <Button onClick={() => navigate(`/month?m=${month}`)}>
              ملخص {monthLabel(month)}
            </Button>
          </>
        }
      />
      {error && (
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل لوحة القيادة.')}
        />
      )}

      <div className="desk-summary-body">
        <div className="desk-kpis">
          {accounts.map((a) => (
            <Link
              key={a.id}
              to={`/operations?account=${a.id}`}
              className="desk-kpi"
            >
              <span className="field-hint">
                {a.name} · {ACCOUNT_TYPE_LABELS[a.type]}
              </span>
              <strong
                className={`desk-amount${isNegative(a.currentBalance) ? ' is-debit' : ''}`}
              >
                {dirhams(a.currentBalance)}
              </strong>
              <span className="field-hint">الرصيد الحالي</span>
            </Link>
          ))}
        </div>

        <div className="desk-summary-grid">
          <Card
            wide
            title={
              <span className="desk-card-title">
                السنة
                <DatePicker
                  picker="year"
                  allowClear={false}
                  value={dayjs(`${year}-01-01`)}
                  onChange={(d) =>
                    d &&
                    setParams(
                      d.format('YYYY') === thisYear
                        ? {}
                        : { year: d.format('YYYY') },
                      { replace: true },
                    )
                  }
                />
              </span>
            }
          >
            {!data ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <div className="desk-year">
                <div className="desk-year-totals">
                  <Total label="المداخيل" value={totals.income} tone="credit" />
                  <Total
                    label="المصاريف"
                    value={totals.expenses}
                    tone="debit"
                  />
                  <Total
                    label="النتيجة"
                    value={totals.result}
                    tone={isNegative(totals.result) ? 'debit' : 'credit'}
                  />
                  <span className="field-hint">
                    بدون التحويلات وأموال القروض. اضغط على شهر لملخصه.
                  </span>
                </div>
                <MonthBars
                  months={months}
                  selected={year === thisYear ? month : null}
                  onPick={(m: MonthKey) => navigate(`/month?m=${m}`)}
                />
              </div>
            )}
          </Card>

          <Card title="ما يجب إكماله">
            {!data ? (
              <Skeleton active />
            ) : (
              <>
                <WorkLine
                  label="تحتاج مراجعة"
                  n={data.review?.totalCount ?? 0}
                  to="/review"
                />
                <WorkLine
                  label="بلا صنف"
                  n={data.noCategory?.totalCount ?? 0}
                  to="/operations?missing=category"
                />
                <WorkLine
                  label="بلا جهة"
                  n={data.noCounterparty?.totalCount ?? 0}
                  to="/operations?missing=counterparty"
                />
                <WorkLine
                  label="مصاريف بلا وصل"
                  n={data.noReceipt?.totalCount ?? 0}
                  to="/operations?missing=receipt"
                />
              </>
            )}
          </Card>

          <Card title="ديون لم تُسوَّ بعد">
            {data && debts.length === 0 && (
              <div className="field-hint">لا ديون متبقية.</div>
            )}
            {debts.map((d) => (
              <Link
                key={d.id}
                to={`/debts?panel=${d.id}`}
                className="desk-line"
              >
                <span>
                  {d.counterparty?.name ?? '—'}
                  <span className="field-hint">
                    {' '}
                    · {DEBT_DIRECTION_LABELS[d.direction]}
                    {d.purpose ? ` · ${d.purpose}` : ''}
                  </span>
                </span>
                {isNegative(d.outstanding) ? (
                  <span className="desk-outstanding is-over">
                    دُفع أكثر بـ {dirhams(d.outstanding.trim().slice(1))}
                  </span>
                ) : (
                  <strong>{dirhams(d.outstanding)}</strong>
                )}
              </Link>
            ))}
          </Card>

          <Card title="آخر ما أُدخل">
            {present(data?.recent?.nodes).map((o) => (
              <Link
                key={o.id}
                to={
                  o.transferId
                    ? `/transfers?panel=${o.transferId}`
                    : `/operations?panel=${o.id}`
                }
                className="desk-line"
              >
                <span className="ltr-num">{o.date}</span>
                <span className="desk-line-text">
                  {o.description || o.category?.name || TYPE_LABELS[o.type]}
                </span>
                <span className={`desk-amount is-${o.type}`}>
                  {o.type === 'credit' ? '+' : '−'} {dirhams(o.amount)}
                </span>
              </Link>
            ))}
          </Card>

          <Card title="هذا الشهر">
            <Link to={`/month?m=${month}`} className="desk-line">
              <span>ملخص {monthLabel(month)}</span>
              <span className="field-hint">حسب الصنف والمشروع والحساب</span>
            </Link>
            <Link
              to={`/operations?from=${monthRange(month)[0]}&to=${monthRange(month)[1]}`}
              className="desk-line"
            >
              <span>عمليات {monthLabel(month)}</span>
            </Link>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Card({
  title,
  wide,
  children,
}: {
  title: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`desk-card${wide ? ' is-wide' : ''}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Total({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'credit' | 'debit';
}) {
  return (
    <div className="desk-total-line">
      <span>{label}</span>
      <strong className={`desk-amount is-${tone}`}>{dirhams(value)}</strong>
    </div>
  );
}

function WorkLine({ label, n, to }: { label: string; n: number; to: string }) {
  return (
    <Link to={to} className={`desk-line${n ? '' : ' is-done'}`}>
      <span>{label}</span>
      <strong className="ltr-num">{n}</strong>
    </Link>
  );
}
